import { projectType } from "../taxonomy";
import {
  AccessDeniedError,
  NotFoundError,
  type NewProjectInput,
  type OrthosApi,
  type ProjectDetail,
  type ProjectSummary,
} from "../types";
import { buildDetail } from "./detail";
import { notifications } from "./notifications";
import { CONFIDENTIAL_PROJECTS } from "./organisations";
import type { ProjectStore } from "./project-store";
import { createMockRestrictions } from "./restrictions";
import type { SessionPort } from "./session-port";

/**
 * In-browser backend so the UI can be built and demonstrated before the Rust services exist. It implements the same
 * OrthosApi the live client will. Everything belongs to the signed-in person's current organisation (FR-PROJ-091,
 * NFR-PLAT-51): projects, people and notifications. Projects you create and pins persist in this browser only.
 */

export interface MockApiOptions {
  latencyMs: number;
  /** Who is signed in, in which organisation; throws SessionExpiredError without a live session. */
  session: SessionPort;
  projects: ProjectStore;
}

export function createMockApi({ latencyMs, session, projects }: MockApiOptions): OrthosApi {
  const now = Date.now();
  const wait = () => new Promise<void>((resolve) => setTimeout(resolve, latencyMs));

  /**
   * Members see every non-confidential project as Viewer by default, or only theirs under the "invite only" policy
   * (FR-ADMIN-012); confidential projects only
   * through their team or an approved request (FR-ADMIN-013, 028); guests only what they were given (FR-ADMIN-024).
   */
  const canSee = (project: ProjectSummary, userId: string, role: string): boolean => {
    if (role === "owner" || role === "admin") return true;
    if (project.team.includes(userId) || session.hasProjectGrant(project.id, userId)) return true;
    if (role === "guest") return false;
    // "Invite only" (the admin console's policy, FR-ADMIN-012): Members see what they were added to, nothing more.
    if (session.membersSeeAllProjects?.(session.current().organisation.id) === false) return false;
    return !CONFIDENTIAL_PROJECTS.has(project.id);
  };

  /** The current organisation's projects this person can see, with their own pins. */
  const visible = (): ProjectSummary[] => {
    const { person, organisation, role } = session.current();
    return projects
      .list(organisation.id, organisation.projectPrefix, person.id)
      .filter((project) => canSee(project, person.id, role))
      .map((project) => ({
        ...project,
        pinned: projects.pinOverride(person.id, project.id) ?? project.pinned,
      }));
  };

  /** One project, or AccessDeniedError when it belongs to another organisation or is not shared with this person. */
  const find = (id: string): ProjectSummary => {
    const project = visible().find((p) => p.id === id);
    if (project !== undefined) return project;
    if (projects.locate(id) !== undefined) throw new AccessDeniedError(id);
    throw new NotFoundError(`Project ${id}`);
  };

  const restrictions = createMockRestrictions({
    find,
    who: () => {
      const { person, role } = session.current();
      return { personId: person.id, role };
    },
    now: () => new Date(),
  });

  const notificationsFor = () => {
    const { person } = session.current();
    return notifications(visible(), now, projects.allExtras()).map((n) => ({
      ...n,
      read: projects.readOverride(person.id, n.id) ?? n.read,
    }));
  };

  return {
    async organisation() {
      await wait();
      return session.current().organisation;
    },
    async me() {
      await wait();
      return session.current().person;
    },
    async people() {
      await wait();
      return session.people(session.current().organisation.id);
    },
    async listProjects() {
      await wait();
      return visible();
    },
    async getProject(id): Promise<ProjectDetail> {
      await wait();
      return restrictions.narrow(buildDetail(find(id), now, projects.extras(id)));
    },
    async createProject(input: NewProjectInput, key: string) {
      await wait();
      const { person, organisation } = session.current();
      const id = projects.create(
        organisation.id,
        organisation.projectPrefix,
        person.id,
        input,
        key,
      );
      return find(id);
    },
    recordStudio(id, summary) {
      projects.setStudio(id, summary);
    },
    async updateProject(id, patch) {
      await wait();
      const project = find(id);
      if (patch.type !== undefined && projectType(patch.type).discipline !== project.discipline)
        throw new Error("Choose a type in the project's discipline.");
      projects.edit(id, patch);
      return find(id);
    },
    async setPinned(id, pinned) {
      await wait();
      find(id);
      projects.setPinned(session.current().person.id, id, pinned);
    },
    async setArchived(id, archived) {
      await wait();
      find(id);
      projects.setArchived(id, archived);
    },
    async addSample() {
      await wait();
      projects.setSample(session.current().organisation.id, true);
      const sample = visible().find((p) => p.sample);
      if (sample === undefined) throw new NotFoundError("Sample project");
      return sample;
    },
    async removeSample(id) {
      await wait();
      if (!find(id).sample)
        throw new Error("Only sample projects can be removed; archive real projects instead.");
      projects.setSample(session.current().organisation.id, false);
    },
    drawingAccess: restrictions.api,
    async listNotifications() {
      await wait();
      return notificationsFor();
    },
    async setNotificationRead(id, read) {
      await wait();
      projects.setRead(session.current().person.id, id, read);
    },
    async markAllNotificationsRead() {
      await wait();
      const { person } = session.current();
      for (const n of notificationsFor()) projects.setRead(person.id, n.id, true);
    },
  };
}
